import { ModelScreenerSkeleton } from "@pages/managed-accounts/model-screener/components/model-screener-skeleton";
import React, { useMemo, useRef } from "react";
import { useMount, useUnmount } from "react-use";
import { useModelScreener } from "@hooks/useModelScreener";
import { SHContainer, SHStack } from "@components/design-systems";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@redux/store";
import { ModelScreenerTable } from "@pages/managed-accounts/model-screener/components/model-screener-table/table";
import { resetTemporaryData } from "@redux/slices/model-screener";
import { isEmpty } from "lodash";

export const ModelScreenerTableLayout = () => {
  const abortController = useRef<AbortController | null>(null);
  const {
    loadModelScreenerData,
    loadModelScreenerFilterSettings,
    loadModelScreenerColumnSettings,
  } = useModelScreener();
  const dispatch = useAppDispatch();
  const isLoading = useSelector(
    (state: RootState) => state.modelScreener.ui.isLoading,
  );
  const data = useSelector(
    (state: RootState) => state.modelScreener.filteredData,
  );

  const shouldShowSkeleton = useMemo(
    () => isLoading || isEmpty(data),
    [isLoading, data],
  );

  useMount(async () => {
    dispatch(resetTemporaryData());
    abortController.current = new AbortController();
    await loadModelScreenerData({ signal: abortController.current.signal });
    await loadModelScreenerFilterSettings();
    await loadModelScreenerColumnSettings();
  });

  useUnmount(() => {
    abortController.current && abortController.current.abort();
  });

  return (
    <SHContainer
      maxWidth={false}
      sx={(theme) => ({
        [theme.breakpoints.down("lg3")]: {
          maxWidth: "lg",
        },
        [theme.breakpoints.up("lg3")]: {
          maxWidth: "95%",
        },
        px: { xs: 0 },
      })}
    >
      <SHStack display={shouldShowSkeleton ? "block" : "none"}>
        <ModelScreenerSkeleton />
      </SHStack>
      <SHStack display={shouldShowSkeleton ? "none" : "block"}>
        <ModelScreenerTable modelScreenerData={data} />
      </SHStack>
    </SHContainer>
  );
};
